import React from 'react';
import { Box, Typography } from '@heycater/design-system';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import StarHalfIcon from '@material-ui/icons/StarHalf';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const HIGHEST_SCORE = 5;
interface Props {
  score: number;
  ratingCount: number;
}

function Rating({ score, ratingCount }: Props) {
  const { i18n } = useTranslation();
  const { t } = useTranslation('static_pages');

  const formatNumberForLocale = (number: number) =>
    number.toLocaleString(i18n.languages[0], {
      minimumFractionDigits: 1,
    });

  const normalizedScore = React.useMemo(() => score / 10, [score]);
  const filledStarsArray = React.useMemo(
    () => Array.from({ length: Math.floor(normalizedScore) }),
    [normalizedScore]
  );
  const emptyStarsArray = React.useMemo(
    () => Array.from({ length: Math.floor(HIGHEST_SCORE - normalizedScore) }),
    [normalizedScore]
  );

  return (
    <Box display="flex" flexWrap="wrap">
      <Box display="flex" flexWrap="wrap" alignItems="center" mr={0.5}>
        <Box color="#007F33">
          <Typography
            mr={1}
            variant="body1"
            color="inherit"
            lineHeight={'26.88px'}
            fontSize={16}
          >
            {formatNumberForLocale(normalizedScore)}
          </Typography>
        </Box>

        <Box lineHeight="normal">
          {filledStarsArray.map((_, index) => (
            <StarIcon color="secondary" key={index} fontSize="small" />
          ))}
          {normalizedScore % 1 !== 0 && (
            <StarHalfIcon color="secondary" fontSize="small" />
          )}
          {emptyStarsArray.map((_, index) => (
            <StarBorderIcon color="secondary" key={index} fontSize="small" />
          ))}
        </Box>
      </Box>
      {ratingCount && (
        <Box color="#686D6B">
          <Typography color="inherit" fontSize={{ xs: 12, md: 16 }}>
            (
            <Underline>
              {t('catererCards.ratingCount', { count: ratingCount })}
            </Underline>
            )
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const Underline = styled.span`
  text-decoration: underline;
`;

export default Rating;
